import React, { PropsWithChildren } from 'react';
import styled, { DefaultTheme, css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { StatusColour, STATUS_COLOURS } from '../../fundamentals/colours/variants';
import { IGapSizeProps, IGutterAsMarginProps, IGutterProps, useColumnGapCss, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { Heading6 } from '../Headings/Headings';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { AlertGroupName, IAlertState } from '../../hooks/useAlertState/useAlertState';
import { StatusIcon } from '../StatusIcon/StatusIcon';
import { CloseButton } from '../CloseButton/CloseButton';
import { A, useZeroPaddingOnChildPsCss } from '../Texts/Texts';
import { FONT_SIZES, useFontSizeCss } from '../../fundamentals/typography/typography';
import { CtaButtonBar } from '../CtaButtonBar/CtaButtonBar';
import { IShadowProps, SHADOW_TYPES, useShadowCss } from '../../fundamentals/shadows/shadows';
import { Button } from '../Button/Button';
import { ColourAssignment, IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv } from '../../common/BaseElements';
import { ResponsiveGapContainer } from '../../fundamentals/spaces/responsive/SpaceContainerResponsive';
import { SpaceContainer } from '../../fundamentals/spaces/SpaceContainer';
import { IBorderRadiusProps, useBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';

export interface IBaseAlertProps extends IAlertState, IGutterAsMarginProps, IShadowProps {
  groupName?: AlertGroupName;
  light?: boolean;
  noIcon?: boolean;
  noInnerMargin?: boolean;
}

type ColourValues = Pick<ColourAssignment, 'text' | 'background' | 'border'>;
const generateColour = (
  { eoraCoreColours }: IColoursCollection,
  variant: StatusColour
): Record<'default' | 'light', ColourValues & { content?: ColourValues }> => {
  const severity = variant.toUpperCase();
  const alertColours = eoraCoreColours.ALERT_COLORS as any;
  const statusColours = eoraCoreColours.STATUS_COLORS as any;
  const colourPrefix = 'COLOR_';
  const alertColourTextPrefix = 'ALERT_COLOR_TEXT_';
  const alertColourBackgroundPrefix = 'ALERT_COLOR_BACKGROUND_';

  return {
    light: {
      text: statusColours[severity][`${colourPrefix}${severity}_600`],
      border: `${PX_TO_REM[4]} solid ${statusColours[severity][`${colourPrefix}${severity}_500`]}`,
      background: eoraCoreColours.BASE.WHITE_NOSWITCH
    },
    default: {
      text: alertColours[`${alertColourTextPrefix}${severity}`],
      border: `${PX_TO_REM[4]} solid ${statusColours[severity][`${colourPrefix}${severity}_500`]}`,
      background: alertColours[`${alertColourBackgroundPrefix}${severity}`],
      content: {
        text: alertColours[`${alertColourTextPrefix}${severity}`]
      }
    }
  };
};

export const alertColours = (colourCollection: IColoursCollection) => ({
  alertColours: {
    [STATUS_COLOURS.NEUTRAL]: {
      ...generateColour(colourCollection, STATUS_COLOURS.INFO) // for legacy purposes, if NEUTRAL is used, revert to info
    },
    [STATUS_COLOURS.SUCCESS]: {
      ...generateColour(colourCollection, STATUS_COLOURS.SUCCESS)
    },
    [STATUS_COLOURS.INFO]: {
      ...generateColour(colourCollection, STATUS_COLOURS.INFO)
    },
    [STATUS_COLOURS.WARNING]: {
      ...generateColour(colourCollection, STATUS_COLOURS.WARNING)
    },
    [STATUS_COLOURS.ERROR]: {
      ...generateColour(colourCollection, STATUS_COLOURS.ERROR)
    }
  }
});

const DEFAULT_ALERT_ICON_SIZE = SIZES.X_SMALL;
const MICRO_ALERT_ICON_SIZE = SIZES.XX_SMALL;

const getAlertSpacing = (theme: DefaultTheme, groupName: AlertGroupName = AlertGroupName.Alert) =>
  groupName === AlertGroupName.Toast &&
  css`
    padding: ${theme.eoraSpaces.large};
  `;

const getToastColours = (theme: DefaultTheme) => ({
  text: theme.colours.eoraCoreColours.BASE.WHITE,
  background: theme.colours.eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_85,
  content: { text: theme.colours.eoraCoreColours.BASE.WHITE },
  border: 'none'
});

const getAlertVariantSelector = ({ theme, variant, light, groupName }: { theme: DefaultTheme } & Pick<IBaseAlertProps, 'variant' | 'light' | 'groupName'>) =>
  groupName === AlertGroupName.Toast ? getToastColours(theme) : theme.alertColours[variant!][light ? `light` : `default`];

interface IAlertBaseStyleProps extends Pick<IBaseAlertProps, 'variant' | 'light' | 'shadow' | 'allowBodyStyleOverride' | 'groupName'> {
  isMicro?: boolean;
}

const AlertContent = styled(BaseDiv)<IGapSizeProps & Omit<IAlertBaseStyleProps, 'shadow'>>`
  width: 100%;

  ${useColumnGapCss()}

  ${({ allowBodyStyleOverride }) => allowBodyStyleOverride && useZeroPaddingOnChildPsCss}

  ${({ allowBodyStyleOverride, isMicro }) =>
    allowBodyStyleOverride &&
    isMicro &&
    css`
      &,
      & *,
      & > * {
        ${useFontSizeCss({ [SIZES.SMALL]: FONT_SIZES.TEXT_MICRO })}
      }
    `}

  &:not(${A}):not(${Button}),
  & *:not(${A}):not(${Button}) {
    ${(props) => {
      const textColourOverride = getAlertVariantSelector(props)?.content?.text;
      return (
        textColourOverride &&
        css`
          color: ${textColourOverride};
        `
      );
    }};
  }
`;

const AlertTitle = styled(Heading6)<IAlertBaseStyleProps>`
  color: ${(props) => getAlertVariantSelector(props).text};
`;

const useAlertCss = () => css<IAlertBaseStyleProps & IGutterProps>`
  align-items: flex-start;
  width: 100%;
  ${({ theme, groupName }) => getAlertSpacing(theme, groupName)};
  ${useGutterAsMarginCss()};

  border-left: ${(props) => getAlertVariantSelector(props).border};
`;

const AlertContainer = styled(ResponsiveGapContainer)<IAlertBaseStyleProps & IBorderRadiusProps & Pick<IBaseAlertProps, 'noInnerMargin'>>`
  ${useAlertCss()};
  ${useShadowCss};
  background: ${(props) => getAlertVariantSelector(props).background};
  ${(props) => useBorderRadiusCss(props.groupName === AlertGroupName.Toast)};
  ${({ noInnerMargin }) =>
    !!noInnerMargin &&
    css`
      margin: 0;
    `};
`;

export const AlertBase = React.forwardRef<HTMLDivElement, PropsWithChildren<IBaseAlertProps>>(
  (
    {
      id,
      title,
      variant = STATUS_COLOURS.INFO,
      light,
      children,
      closable = false,
      onClose,
      groupName = AlertGroupName.Alert,
      allowBodyStyleOverride = true,
      ctaButtons,
      severityIconOutline,
      ctaFullOnMobile = false,
      shadow,
      micro,
      noIcon,
      noInnerMargin = false,
      ...rest
    },
    ref
  ) => {
    const { generateTestId } = useTestId(rest, `AlertBase`);
    const isToast = groupName === AlertGroupName.Toast;
    const isMicro = micro || isToast || groupName === AlertGroupName.MicroAlert;
    const computedVariant = variant === STATUS_COLOURS.NEUTRAL && !isToast ? STATUS_COLOURS.INFO : variant;
    const styleProps = { variant: computedVariant, light, groupName };
    const iconSize = isMicro ? MICRO_ALERT_ICON_SIZE : DEFAULT_ALERT_ICON_SIZE;
    const containerGap = isMicro ? 'xsmall' : 'medium';
    const containerGutter = isToast ? false : containerGap;

    return (
      <SpaceContainer {...rest} {...generateTestId()}>
        <AlertContainer
          {...styleProps}
          shadow={shadow ?? (light ? SHADOW_TYPES.DEFAULT : false)}
          ref={ref}
          isMicro={isMicro}
          gap={containerGap}
          gutter={containerGutter}
          noInnerMargin={noInnerMargin}
        >
          {!noIcon && <StatusIcon outline={severityIconOutline} variant={computedVariant} innerFill={isToast} size={iconSize} {...generateTestId(`icon`)} />}
          <AlertContent gap={isMicro ? 8 : 16} {...styleProps} allowBodyStyleOverride={allowBodyStyleOverride} isMicro={isMicro}>
            {!!title && (
              <AlertTitle headingType='SEMI_BOLD' gutter={false} {...styleProps}>
                {title}
              </AlertTitle>
            )}
            {children}
            {!isMicro && <CtaButtonBar ctaButtons={ctaButtons} ctaFullOnMobile={ctaFullOnMobile} {...generateTestId(`CTA`)} />}
          </AlertContent>
          {closable && <CloseButton size={iconSize} onClick={onClose} variant={isToast ? 'white50' : 'black50'} mode='minimal' {...generateTestId(`close`)} />}
        </AlertContainer>
      </SpaceContainer>
    );
  }
);
